export const PLAN_COMPLEXITY_ORDER = ['MINIMAL', 'STANDARD', 'FULL'];

export const PLAN_COMPLEXITY = Object.freeze({
  MINIMAL: 'MINIMAL',
  STANDARD: 'STANDARD',
  FULL: 'FULL',
});

export const PLAN_COMPLEXITY_RANK = Object.freeze(
  Object.fromEntries(PLAN_COMPLEXITY_ORDER.map((level, index) => [level, index])),
);

export function isPlanComplexity(value) {
  return typeof value === 'string' && PLAN_COMPLEXITY_ORDER.includes(value);
}

/**
 * 러너 실행 엔진 타입(RunnerType)의 단일 진실 소스(SSOT).
 * api·web·daemon은 로컬에 값 목록을 두지 말고 이 상수를 소비/재-export한다.
 * 값 추가/삭제/개명은 반드시 이곳 한 곳에서만 수행한다.
 */
export const RUNNER_TYPES = Object.freeze({
  OPENCODE: 'OPENCODE',
  CLAUDE_CODE: 'CLAUDE_CODE',
  CODEX: 'CODEX',
  ANTIGRAVITY: 'ANTIGRAVITY',
  AMP: 'AMP',
  COPILOT_CLI: 'COPILOT_CLI',
  CURSOR_CLI: 'CURSOR_CLI',
  KIMI_CLI: 'KIMI_CLI',
  KIRO_CLI: 'KIRO_CLI',
  GROK_BUILD: 'GROK_BUILD',
  OMP: 'OMP',
  MUSE_CODE: 'MUSE_CODE',
  // TODO: AIDER, GOOSE, PLANDEX
});

/**
 * 신규 실행 대상에서는 제외됐지만, 과거 기록(트리거/러너모델/리포트 등) 직렬화를 위해
 * 응답 스키마에서는 계속 허용해야 하는 폐기된 러너 타입 목록.
 * 예: GEMINI CLI 서비스 종료로 신규 선택은 막되, 기존 GEMINI 스냅샷 조회는 유지한다.
 */
export const DEPRECATED_RUNNER_TYPES = Object.freeze(['GEMINI']);

/**
 * 응답(직렬화) 스키마 전용 러너 타입 목록.
 * 현행 RunnerType + 폐기 타입을 포함해 과거 스냅샷 조회가 깨지지 않도록 한다.
 * 요청(입력) 검증에는 RUNNER_TYPES(현행)만 사용한다.
 */
export const runnerTypeResponseValues = Object.freeze([...Object.values(RUNNER_TYPES), ...DEPRECATED_RUNNER_TYPES]);

const NORMALIZED_RUNNER_TYPES = new Map([
  ...runnerTypeResponseValues.map((runnerType) => [runnerType, runnerType]),
  ['CLAUDE', RUNNER_TYPES.CLAUDE_CODE],
  ['ANTIGRAVITY_CLI', RUNNER_TYPES.ANTIGRAVITY],
  ['KIMI', RUNNER_TYPES.KIMI_CLI],
  ['COPILOT', RUNNER_TYPES.COPILOT_CLI],
  ['CURSOR', RUNNER_TYPES.CURSOR_CLI],
  ['KIRO', RUNNER_TYPES.KIRO_CLI],
  ['GROK', RUNNER_TYPES.GROK_BUILD],
  ['MUSE', RUNNER_TYPES.MUSE_CODE],
]);

/**
 * `normalizeRunnerType`이 인식하는 정규화 키 전체(정규 값 + 별칭, 대문자·`_` 구분자 형태).
 * cli는 이 패키지에 의존할 수 없어 `cli/src/utils/runnerTypeAliases.ts`에 같은 규칙을 미러링하며,
 * `scripts/cli-runner-type-alias-contract.test.mjs`가 이 목록으로 두 구현을 대조한다.
 */
export const RUNNER_TYPE_NORMALIZATION_INPUTS = Object.freeze([...NORMALIZED_RUNNER_TYPES.keys()]);

/**
 * 엔진 표기의 대소문자·구분자 변형과 알려진 별칭을 현행·폐기 러너 타입으로 정규화한다.
 * 엔진을 특정할 수 없는 값은 null을 반환해 호출부가 원문 보존 여부를 결정하도록 한다.
 */
export function normalizeRunnerType(value) {
  if (typeof value !== 'string') {
    return null;
  }

  return NORMALIZED_RUNNER_TYPES.get(value.trim().toUpperCase().replaceAll('-', '_')) ?? null;
}

/**
 * 러너 타입별로 요청 가능한 추론 강도(Effort) 레벨의 전체 허용 집합(SSOT).
 * 실제 요청 가능 레벨은 각 모델의 supportedEffortLevels(이 집합의 부분집합)로 다시 제한된다.
 * 여기에 없는 러너 타입은 Effort를 지원하지 않는다(빈 배열 취급).
 * 값은 소문자로 정규화한다. CLI 계약: CODEX=`-c model_reasoning_effort="<level>"`, CLAUDE_CODE=`--effort <level>`,
 * MUSE_CODE=`exec --reasoning-effort <level>`.
 * 값 추가/삭제는 반드시 이곳 한 곳에서만 수행하고 api·web·daemon은 이 상수를 소비한다.
 *
 * 이 객체의 키 집합은 `daemon/src/runners/capabilities.ts`의 `RUNNER_CAPABILITIES.effort`와
 * 1:1이어야 한다(`scripts/runner-capability-contract.test.mjs`가 대조한다). 새 엔진의 허용 집합은
 * 실제 인자 전달 구현과 같은 커밋에서 추가한다 — 여기에만 추가하면 계약 테스트가 실패한다.
 */
export const RUNNER_EFFORT_LEVELS = Object.freeze({
  // Muse Code 1.0.3, 2026-09-06 실측.
  // `muse exec --help`는 none|minimal|low|medium|high|xhigh|max|ultra를 안내하지만, 도움말이 아니라
  // 실행 계약을 따른다. 기본 공급자(`--provider meta`)에서 `none`은 exit 2로 거부된다:
  //   "--reasoning-effort none is not supported with --provider meta;
  //    choose minimal|low|medium|high|xhigh|max|ultra"
  // MSP model/list 카탈로그 4모델(muse-spark-1.2/1.3 및 각 -contributor) × 8레벨 32회 실행 실측에서
  // `none`만 4모델 전부 실패했고 나머지 7레벨은 4모델 전부 completed였다.
  // 2026-09-04(1.0.2) 기준 목록에 있던 `none`을 빼고 누락돼 있던 `max`를 넣는다.
  [RUNNER_TYPES.MUSE_CODE]: Object.freeze(['minimal', 'low', 'medium', 'high', 'xhigh', 'max', 'ultra']),
  [RUNNER_TYPES.CODEX]: Object.freeze(['minimal', 'low', 'medium', 'high', 'xhigh', 'max', 'ultra']),
  [RUNNER_TYPES.CLAUDE_CODE]: Object.freeze(['low', 'medium', 'high', 'xhigh', 'max']),
  [RUNNER_TYPES.OPENCODE]: Object.freeze(['none', 'minimal', 'low', 'medium', 'high', 'xhigh', 'max']),
  [RUNNER_TYPES.COPILOT_CLI]: Object.freeze(['none', 'minimal', 'low', 'medium', 'high', 'xhigh', 'max']),
  [RUNNER_TYPES.OMP]: Object.freeze(['minimal', 'low', 'medium', 'high', 'xhigh', 'max']),
  [RUNNER_TYPES.ANTIGRAVITY]: Object.freeze(['low', 'medium', 'high']),
  [RUNNER_TYPES.GROK_BUILD]: Object.freeze(['low', 'medium', 'high', 'xhigh']),
});

/**
 * 특정 러너 타입이 Effort(추론 강도) 선택을 지원하는지 여부.
 * RUNNER_EFFORT_LEVELS에 비어 있지 않은 집합이 정의된 경우에만 true.
 */
export function isEffortRunnerType(runnerType) {
  const levels = RUNNER_EFFORT_LEVELS[runnerType];
  return Array.isArray(levels) && levels.length > 0;
}

/**
 * 러너 타입 기준으로 Effort 레벨 값이 전체 허용 집합에 속하는지 검증한다.
 * 모델별 supportedEffortLevels 부분집합 검증과는 별개의 1차 게이트다.
 */
export function isAllowedEffortLevel(runnerType, level) {
  const levels = RUNNER_EFFORT_LEVELS[runnerType];
  return Array.isArray(levels) && levels.includes(level);
}

/**
 * 엔진별 Effort 인자 전달을 처음 구현한 `@agentteams/runner` 최소 버전(SSOT).
 * 러너는 사용자가 개별 설치·갱신하므로, 서버가 허용한 Effort를 대상 러너가 아직 전달하지 못하는
 * 전환 기간이 길다. 그 러너의 trigger handler는 `capabilities.effort=false`라 값을 무음 폐기하고
 * CLI 기본 강도로 실행하므로, API는 이 표로 대상 러너 버전을 확인해 요청을 미리 거절한다.
 * 여기에 없는 엔진은 Effort 전달이 러너 도입기부터 있었으므로 버전 게이트가 없다.
 * 새 엔진에 Effort를 붙이면 그 변경이 실제로 나가는 러너 릴리스 버전을 여기에 추가한다.
 */
export const RUNNER_EFFORT_MIN_RUNNER_VERSION = Object.freeze({
  // 0.0.126이 마지막 미지원 릴리스이므로, 이 5개 엔진의 전달 구현은 0.0.127부터 실린다.
  [RUNNER_TYPES.OPENCODE]: '0.0.127',
  [RUNNER_TYPES.COPILOT_CLI]: '0.0.127',
  [RUNNER_TYPES.OMP]: '0.0.127',
  [RUNNER_TYPES.ANTIGRAVITY]: '0.0.127',
  [RUNNER_TYPES.GROK_BUILD]: '0.0.127',
});

/**
 * 모델 식별자 자체에 추론 강도가 박혀 있는 엔진의 접미사 패턴(SSOT).
 * ANTIGRAVITY `agy models`(1.1.27 실측)의 id는 `gemini-3.8-flash-high`처럼 레벨 접미사를 갖고,
 * 접미사와 다른 `--effort`를 주면 CLI가 `conflicts with --effort=<level>`로 exit 1 한다.
 * 근거와 실측 기록은 `daemon/RUNNER_PERMISSIONS.md`의 지원 매트릭스에 있다.
 */
const EMBEDDED_EFFORT_LEVEL_PATTERNS = Object.freeze({
  [RUNNER_TYPES.ANTIGRAVITY]: /-(low|medium|high)$/u,
});

/**
 * 모델 식별자에 박혀 있는 추론 강도 레벨. 없으면 null.
 * 탐지 보고의 supportedEffortLevels 보완과 저장·요청 검증이 같은 판정을 쓰도록 여기 한 벌만 둔다.
 */
export function resolveEmbeddedEffortLevel(runnerType, modelValue) {
  const pattern = EMBEDDED_EFFORT_LEVEL_PATTERNS[runnerType];
  if (!pattern || typeof modelValue !== 'string') {
    return null;
  }

  return pattern.exec(modelValue.trim().toLowerCase())?.[1] ?? null;
}

/**
 * 모델 식별자에 레벨이 박혀 있는 경우, 요청·저장하려는 Effort 레벨이 그 레벨과 충돌하지 않는지 판정한다.
 * 레벨이 박혀 있지 않은 모델은 항상 true(엔진 허용 집합·모델 메타데이터 검증이 따로 게이트한다).
 */
export function isEffortLevelCompatibleWithModel(runnerType, modelValue, level) {
  const embedded = resolveEmbeddedEffortLevel(runnerType, modelValue);
  return embedded === null || embedded === level;
}

/**
 * 상태 기반 Runner 디스패치 정책이 지원하는 엔티티와 상태의 단일 진실 소스(SSOT).
 * api의 정책 검증과 web의 정책 설정 UI는 이 값을 함께 소비한다.
 */
export const RUNNER_DISPATCH_POLICY_STATES = Object.freeze({
  PLAN: Object.freeze(['BACKLOG', 'TODO', 'ASSIGNED', 'IN_PROGRESS', 'PARTIAL', 'DONE', 'BLOCKED', 'CANCELLED']),
  CO_ACTION: Object.freeze(['OPEN', 'CLOSED']),
  COMPLETION_REPORT: Object.freeze(['COMPLETED', 'FAILED', 'PARTIAL', 'CANCELLED']),
  POST_MORTEM: Object.freeze(['OPEN', 'IN_PROGRESS', 'RESOLVED']),
  CODE_REVIEW: Object.freeze(['PENDING', 'OPEN', 'PARTIAL', 'COMPLETED', 'CANCELLED', 'FAILED']),
});

export const RUNNER_DISPATCH_POLICY_ENTITY_TYPES = Object.freeze(Object.keys(RUNNER_DISPATCH_POLICY_STATES));

// 러너 히스토리 마크다운의 마지막 "### Questions for User" 섹션 본문 줄을 원문 그대로(들여쓰기 포함) 모은다.
// 마크다운 렌더와 항목 추출이 같은 섹션을 보도록 스캔은 여기 한 벌만 둔다.
// api(목록 응답의 불리언 산출)와 web(상세 모달 렌더)이 동일 판정을 쓰도록 이 패키지가 SSOT다.
function collectQuestionsForUserLines(historyMarkdown) {
  if (!historyMarkdown) {
    return [];
  }

  const lines = historyMarkdown.split(/\r?\n/);
  const sectionHeaderPattern = /^###\s+Questions for User\s*$/i;
  // 레벨과 무관하게 어떤 헤딩이든 섹션을 끝낸다. ###만 종료로 보면 "## Next Session" 같은
  // 상위 헤딩이 섹션을 닫지 못해 그 뒤 내용이 통째로 질문 목록에 딸려온다.
  const headingPattern = /^#{1,6}\s+/;
  // 코드 펜스 안의 "# 주석"은 헤딩이 아니다. 펜스를 추적하지 않으면 질문에 포함된
  // 셸 명령 예시가 섹션을 조기 종료시킨다.
  const fencePattern = /^\s*(```|~~~)/;

  let sectionLines = [];
  let inSection = false;
  let inFence = false;

  for (const line of lines) {
    if (fencePattern.test(line)) {
      inFence = !inFence;
      if (inSection) {
        sectionLines.push(line);
      }
      continue;
    }

    if (!inFence && sectionHeaderPattern.test(line.trim())) {
      inSection = true;
      sectionLines = [];
      continue;
    }

    if (!inSection) {
      continue;
    }

    if (!inFence && headingPattern.test(line.trim())) {
      inSection = false;
      continue;
    }

    sectionLines.push(line);
  }

  return sectionLines;
}

// 목록 기호(-, *, 1.)를 떼고 남은 질문 본문. 자리표시자 판정과 항목 추출이 같은 기준을 쓰도록 한 벌만 둔다.
function normalizeQuestionLine(line) {
  return line
    .trim()
    .replace(/^([-*]|\d+\.)\s+/, '')
    .trim();
}

function isPlaceholderQuestion(value) {
  return (
    /^none\.?$/i.test(value) ||
    /^n\/a\.?$/i.test(value) ||
    /^(?:없음|없습니다|해당\s*없음|특이사항\s*없음)[.!?]?$/.test(value)
  );
}

export function extractQuestionsForUser(historyMarkdown) {
  const items = [];

  for (const line of collectQuestionsForUserLines(historyMarkdown)) {
    const normalized = normalizeQuestionLine(line);
    if (normalized.length > 0 && !isPlaceholderQuestion(normalized)) {
      items.push(normalized);
    }
  }

  return items;
}

// 섹션 본문을 마크다운 원문으로 돌려준다. 목록 중첩·강조·링크가 살아 있어야 뷰어가 제대로 렌더한다.
// None./N/A 자리표시자 줄은 extractQuestionsForUser와 동일하게 걸러, 두 함수가 같은 내용을 가리키게 한다.
// 남는 본문이 없으면(자리표시자뿐이거나 섹션이 없으면) null이라 호출부가 블록을 숨긴다.
export function extractQuestionsForUserMarkdown(historyMarkdown) {
  const body = collectQuestionsForUserLines(historyMarkdown)
    .filter((line) => !isPlaceholderQuestion(normalizeQuestionLine(line)))
    .join('\n')
    .trim();

  return body.length > 0 ? body : null;
}

// 목록 배지처럼 "질문이 있는가"만 필요한 호출부용 술어.
// 항목 추출과 같은 함수를 재사용해 배열/불리언 판정이 서로 갈라지지 않게 한다.
export function hasQuestionsForUser(historyMarkdown) {
  return extractQuestionsForUser(historyMarkdown).length > 0;
}
